// frontend/src/modules/comment-monitor/CommentsModal.tsx
import React from 'react';
import type { CommentWithStatus } from './useCommentMonitor.ts';

interface Props {
  comments: CommentWithStatus[];
  loading: boolean;
  onClose: () => void;
  onHide: (id: string) => void;
  onHideMultiple: (ids: string[]) => void;
  isHidingMultiple: boolean;
  onRead: (id: string) => void;
}

export function CommentsModal({ comments, loading, onClose, onHide, onHideMultiple, isHidingMultiple, onRead }: Props) {
  const [activeTab, setActiveTab] = React.useState<'visible' | 'hidden'>('visible');
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new window.Set());

  const visibleComments = comments.filter(c => !c.isHidden);
  const hiddenComments = comments.filter(c => c.isHidden);

  const displayedComments = activeTab === 'visible' ? visibleComments : hiddenComments;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div 
        className="glass-card"
        style={{
          width: '100%',
          maxWidth: '600px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          padding: 'var(--space-5)',
          overflow: 'hidden', // to allow scrolling inside
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
          <h2 style={{ fontSize: '1.25rem', margin: 0 }}>Останні коментарі</h2>
          <button 
            className="btn btn--secondary" 
            style={{ padding: '4px 8px' }}
            onClick={onClose}
          >
            Закрити
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-4)', borderBottom: '1px solid var(--color-border)' }}>
          <button 
            style={{ 
              background: 'none', 
              border: 'none', 
              color: activeTab === 'visible' ? 'var(--color-primary)' : 'var(--color-text-subtle)', 
              padding: '8px 16px',
              borderBottom: activeTab === 'visible' ? '2px solid var(--color-primary)' : '2px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'visible' ? 600 : 'normal'
            }}
            onClick={() => setActiveTab('visible')}
          >
            Активні ({visibleComments.length})
          </button>
          <button 
            style={{ 
              background: 'none', 
              border: 'none', 
              color: activeTab === 'hidden' ? 'var(--color-primary)' : 'var(--color-text-subtle)', 
              padding: '8px 16px',
              borderBottom: activeTab === 'hidden' ? '2px solid var(--color-primary)' : '2px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'hidden' ? 600 : 'normal'
            }}
            onClick={() => setActiveTab('hidden')}
          >
            Приховані ({hiddenComments.length})
          </button>
        </div>

        {activeTab === 'visible' && displayedComments.length > 0 && !loading && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.875rem' }}>
              <input
                type="checkbox"
                checked={selectedIds.size === displayedComments.length && displayedComments.length > 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedIds(new window.Set(displayedComments.map(c => c.id)));
                  } else {
                    setSelectedIds(new window.Set());
                  }
                }}
              />
              Обрати всі
            </label>
            {selectedIds.size > 0 && (
              <button 
                className="btn btn--secondary"
                style={{ padding: '4px 12px', fontSize: '0.75rem', borderColor: 'var(--color-error)', color: 'var(--color-error)' }}
                disabled={isHidingMultiple}
                onClick={() => {
                  if (confirm(`Приховати ${selectedIds.size} обраних коментарів?`)) {
                    onHideMultiple(Array.from(selectedIds));
                    setSelectedIds(new window.Set());
                  }
                }}
              >
                {isHidingMultiple ? 'Приховування...' : `Приховати обрані (${selectedIds.size})`}
              </button>
            )}
          </div>
        )}

        <div style={{ overflowY: 'auto', flex: 1, paddingRight: 'var(--space-2)' }}>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {[1, 2, 3].map(i => (
                 <div key={i} className="skeleton" style={{ height: 80, borderRadius: 'var(--radius-sm)' }} />
              ))}
            </div>
          ) : displayedComments.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state__title">Немає коментарів</div>
              <div className="empty-state__desc">
                {activeTab === 'visible' ? 'Тут поки пусто.' : 'Немає прихованих коментарів.'}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {displayedComments.map(c => {
                const date = c.createdTime
                  ? new Intl.DateTimeFormat('uk-UA', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    }).format(new Date(c.createdTime))
                  : null;

                return (
                  <div 
                    key={c.id} 
                    style={{ 
                      padding: 'var(--space-3)', 
                      background: 'var(--color-surface-2)', 
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--color-border)',
                      position: 'relative',
                    }}
                  >
                    {!c.isRead && (
                      <div 
                        style={{
                          position: 'absolute',
                          top: -4,
                          right: -4,
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          background: 'var(--color-primary)',
                          boxShadow: '0 0 8px var(--color-primary)',
                        }}
                        title="Новий коментар"
                      />
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {activeTab === 'visible' && (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(c.id)}
                            onChange={(e) => {
                              const next = new window.Set(selectedIds);
                              if (e.target.checked) next.add(c.id);
                              else next.delete(c.id);
                              setSelectedIds(next);
                            }}
                          />
                        )}
                        <span className="text-sm" style={{ fontWeight: 600, color: 'var(--color-text)' }}>
                          {c.fromName ?? 'Невідомий користувач'}
                        </span>
                      </div>
                      {date && <span className="text-xs text-subtle">{date}</span>}
                    </div>
                    
                    <p className="text-sm" style={{ marginBottom: 'var(--space-3)', wordBreak: 'break-word', color: activeTab === 'hidden' ? 'var(--color-text-subtle)' : 'inherit' }}>
                      {c.message}
                    </p>
                    
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 'var(--space-3)' }}>
                      {c.createdTime && (
                        <span className="text-xs text-subtle">{new Date(c.createdTime).toLocaleString()}</span>
                      )}
                      {c.likeCount > 0 && (
                        <span className="text-xs text-subtle">{c.likeCount} Likes</span>
                      )}
                      
                      {activeTab === 'visible' ? (
                        <>
                          {!c.isRead && (
                            <button 
                              className="btn btn--outline" 
                              style={{ padding: '2px 8px', fontSize: '0.75rem', borderColor: '#10b981', color: '#10b981' }}
                              onClick={() => onRead(c.id)}
                            >
                              Залишити
                            </button>
                          )}
                          <button 
                            className="btn btn--secondary" 
                            style={{ padding: '2px 8px', fontSize: '0.75rem', borderColor: 'var(--color-error)', color: 'var(--color-error)' }}
                            onClick={() => {
                              if(confirm('Ви впевнені що хочете приховати цей коментар на Facebook?')) {
                                onHide(c.id);
                              }
                            }}
                          >
                            Приховати
                          </button>
                        </>
                      ) : (
                        <span className="text-xs" style={{ color: 'var(--color-warning)', padding: '2px 8px', border: '1px solid var(--color-warning)', borderRadius: 'var(--radius-sm)' }}>
                          Приховано
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
